import React, { useState, useRef, useEffect, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import {
  getCoffeeChatBannerList, 
  createCoffeeChatBanner, 
  updateCoffeeChatBanner, 
  deleteCoffeeChatBanner,
  getAdminCoffeeChatUI,
  CoffeeChatBanner,
  CoffeeChatSection,
  CoffeeChatBannerCreateRequest,
  CoffeeChatBannerUpdateRequest,
  getCoffeeChatMentorList,
  getCoffeeChatCurationList,
  createCoffeeChatCuration,
  updateCoffeeChatCuration,
  deleteCoffeeChatCuration,
  addPersonToCoffeeChatCuration,
  removePersonFromCoffeeChatCuration,
  MentorInfo
} from '../../services/admin/coffeeChatBannerApi';
import { AdminPage, AdminPageHeader, AdminPageTitle, AdminSection } from './common/AdminPrimitives';
import {
  Section,
  SectionHeader,
  SectionTitle,
  ErrorMessage,
  LoadingMessage,
  BannerContent,
  DragHandle,
  BannerImage,
  BannerForm,
  TitleRow,
  ColorInputWrapper,
  ColorPicker,
  ColorTextInput,
  CaptionRow,
  ImageUploadWrapper,
  ImagePlaceholder,
  UploadIcon,
  UploadedImage,
  InputFieldWrapper,
  StyledButton,
  ActionButton,
  SectionActions,
  SaveButton,
  CurationContent,
  CurationForm,
  FormGroup,
  CurationButtons,
  SaveIndividualButton,
  LectureGrid,
  LectureCard,
  LectureCardImage,
  NoImage,
  LectureCardContent,
  LectureTitle,
  LecturerName,
  LectureTags,
  Tag,
  RemoveLectureButton,
  AddLectureCard,
  LectureModal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  LectureList,
  LectureListItem,
  LectureListImage,
  LectureListContent,
  AddedIndicator,
} from './CoffeeChatManagementStyles';

// 로컬 배너 타입 (API 타입과 호환되도록 수정)
interface LocalBanner {
  id: string | number; // 문자열(임시) 또는 숫자(서버)
  title: string;
  description: string;
  banner_image_url: string; // API 호환
  font_color_hex_code: string; // API 호환
  color?: string; // UI용 추가 필드
  imageUrl?: string; // UI용 추가 필드
  imageFile?: File; // 업로드할 파일
  isNew?: boolean; // 새로 추가된 배너인지 여부
  isDirty?: boolean; // 수정된 배너인지 여부
}

// 커피챗 큐레이션 관련 타입 (홈 관리와 동일)
interface CoffeeChatCurationSet {
  id: string | number;
  title: string;
  description: string;
  mentors: {
    id: number;
    relation_id?: number; // API 삭제를 위한 relation_id 추가
    name: string;
    department: string;
    image_url: string;
    tags: string[];
  }[];
  isNew?: boolean;
  isDirty?: boolean;
  isExpanded?: boolean; // 멘토 선택 모달 확장 여부
}

// 배너 플러스 아이콘 (32x32, #448181 색상)
const BannerPlusIcon: React.FC = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <path d="M15.9994 4.13725C16.6622 4.13725 17.1994 4.67451 17.1994 5.33725V14.8039H26.6661C27.3288 14.8039 27.8661 15.3412 27.8661 16.0039C27.8661 16.6667 27.3288 17.2039 26.6661 17.2039H17.1994V26.6706C17.1994 27.3333 16.6622 27.8706 15.9994 27.8706C15.3367 27.8706 14.7994 27.3333 14.7994 26.6706V17.2039H5.33281C4.67007 17.2039 4.13281 16.6667 4.13281 16.0039C4.13281 15.3412 4.67007 14.8039 5.33281 14.8039H14.7994V5.33725C14.7994 4.67451 15.3367 4.13725 15.9994 4.13725Z" fill="#448181"/>
  </svg>
);

// 플러스 아이콘 (24x24로 스케일 조정, #448181 색상)
const PlusIcon: React.FC = () => (
  <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15.9994 4.13721C16.6622 4.13721 17.1994 4.67447 17.1994 5.33721V14.8039H26.6661C27.3288 14.8039 27.8661 15.3411 27.8661 16.0039C27.8661 16.6666 27.3288 17.2039 26.6661 17.2039H17.1994V26.6705C17.1994 27.3333 16.6622 27.8705 15.9994 27.8705C15.3367 27.8705 14.7994 27.3333 14.7994 26.6705V17.2039H5.33281C4.67007 17.2039 4.13281 16.6666 4.13281 16.0039C4.13281 15.3411 4.67007 14.8039 5.33281 14.8039H14.7994V5.33721C14.7994 4.67447 15.3367 4.13721 15.9994 4.13721Z" fill="#448181"/>
  </svg>
);

// 마이너스 아이콘 (24x24, #448181 색상)
const MinusIcon: React.FC = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M3.09961 11.9995C3.09961 11.5025 3.50255 11.0995 3.99961 11.0995H19.9995C20.4966 11.0995 20.8995 11.5025 20.8995 11.9995C20.8995 12.4966 20.4966 12.8995 19.9995 12.8995H3.99961C3.50255 12.8995 3.09961 12.4966 3.09961 11.9995Z" fill="#448181"/>
  </svg>
);

// 드래그 핸들 아이콘 (기존 UI에 방해되지 않게 작게)
const DragHandleIcon: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ cursor: 'grab' }}>
    <circle cx="3" cy="3" r="1" fill="#ccc"/>
    <circle cx="9" cy="3" r="1" fill="#ccc"/>
    <circle cx="3" cy="6" r="1" fill="#ccc"/>
    <circle cx="9" cy="6" r="1" fill="#ccc"/>
    <circle cx="3" cy="9" r="1" fill="#ccc"/>
    <circle cx="9" cy="9" r="1" fill="#ccc"/>
  </svg>
);

interface ImageUploadProps {
  className?: string;
  onImageChange?: (imageUrl: string | null, file?: File) => void;
  imageUrl?: string;
}

interface InputFieldProps {
  placeholder: string;
  type?: string;
  className?: string;
  value?: string;
  onChange?: (value: string) => void;
}

interface ButtonProps {
  children: React.ReactNode;
  variant?: 'outlined' | 'primary' | 'text';
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
}

// 이미지 업로드 컴포넌트
const ImageUpload: React.FC<ImageUploadProps> = ({ className = "", onImageChange, imageUrl }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageClick = () => {
    // /// console.log('Image upload clicked');
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // /// console.log('File selected:', file);
    if (file) {
      const url = URL.createObjectURL(file);
      // /// console.log('Image URL created:', url);
      onImageChange?.(url, file);
    }
  };

  // 배너용인지 카드용인지 구분하여 아이콘 선택
  const isBanner = className.includes('bannerImage');

  return (
    <ImageUploadWrapper className={className} onClick={handleImageClick}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
      <ImagePlaceholder>
        {imageUrl ? (
          <UploadedImage src={imageUrl} alt="Uploaded" />
        ) : (
          <UploadIcon>
            {isBanner ? <BannerPlusIcon /> : <PlusIcon />}
          </UploadIcon>
        )}
      </ImagePlaceholder>
    </ImageUploadWrapper>
  );
};

// 입력 필드 컴포넌트
const InputField: React.FC<InputFieldProps> = ({
  placeholder,
  type = "text",
  className = "",
  value,
  onChange
}) => (
  <InputFieldWrapper className={className}>
    <input
      type={type}
      placeholder={placeholder}
      className="input"
      value={value}
      onChange={(e) => onChange?.(e.target.value)}
    />
  </InputFieldWrapper>
);

// 버튼 컴포넌트
const Button: React.FC<ButtonProps> = ({ children, variant = "outlined", onClick, className = "", disabled = false }) => (
  <StyledButton
    variant={variant}
    $disabled={disabled}
    className={className}
    onClick={onClick}
    disabled={disabled}
  >
    {children}
  </StyledButton>
);

// 배너 섹션 컴포넌트
const BannerSection: React.FC<{ 
  onRefreshSections: () => Promise<any>;
  createEmptyBanner: () => LocalBanner;
  sections: CoffeeChatSection[];
}> = ({ onRefreshSections, createEmptyBanner, sections }) => {
  const [banners, setBanners] = useState<LocalBanner[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 커피챗 UI 데이터 조회 (상위에서 받은 함수 사용)
  const loadCoffeeChatUIData = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await onRefreshSections();
      if (result) {
        setBanners(result.banners);
      }
      
    } catch (err: any) {
      /// console.error('커피챗 UI 데이터 조회 실패:', err);
      setError(err.message || '커피챗 UI 데이터를 불러올 수 없습니다.');
      setBanners([createEmptyBanner()]);
    } finally {
      setLoading(false);
    }
  }, [onRefreshSections]);

  // 컴포넌트 마운트 시 커피챗 UI 데이터 조회
  useEffect(() => {
    loadCoffeeChatUIData();
  }, [loadCoffeeChatUIData]);

  // 배너 추가
  const addBanner = () => {
    if (banners.length < 4) {
      const newBanner = createEmptyBanner();
      setBanners(prev => [...prev, newBanner]);
    }
  };

  // 배너 삭제 (즉시 삭제)
  const removeBanner = async (bannerId: string | number) => {
    if (banners.length <= 1) return;

    try {
      const banner = banners.find(b => b.id === bannerId);
      if (!banner) return;

      // 서버에 저장된 배너인 경우 API 호출
      if (typeof bannerId === 'number' && !banner.isNew) {
        await deleteCoffeeChatBanner(bannerId);
      }

      setBanners(prev => prev.filter(banner => banner.id !== bannerId));
    } catch (err: any) {
      /// console.error('커피챗 배너 삭제 실패:', err);
      alert(err.message || '커피챗 배너 삭제에 실패했습니다.');
    }
  };

  // 배너 업데이트
  const updateBannerLocal = (bannerId: string | number, field: string, value: string | File) => {
    /// console.log('배너 업데이트 호출:', { bannerId, field, value });
    
    setBanners(prev => prev.map(banner => {
      if (banner.id !== bannerId) return banner;

      const updates: Partial<LocalBanner> = { isDirty: true };

      if (field === 'imageFile' && value instanceof File) {
        updates.imageFile = value;
        updates.imageUrl = URL.createObjectURL(value);
        updates.banner_image_url = URL.createObjectURL(value);
        /// console.log('이미지 파일 업데이트:', { bannerId, file: value, url: updates.imageUrl });
      } else if (typeof value === 'string') {
        if (field === 'title') {
          updates.title = value;
        } else if (field === 'description') {
          updates.description = value;
        } else if (field === 'color') {
          updates.color = value;
          updates.font_color_hex_code = value;
        }
      }

      const updatedBanner = { ...banner, ...updates };
      /// console.log('배너 업데이트 완료:', updatedBanner);
      return updatedBanner;
    }));
  };

  // 모든 배너 저장 (일괄 저장)
  const saveAllBanners = async () => {
    try {
      setLoading(true);
      setError(null);

      // 저장할 내용이 있는지 먼저 확인
      const hasNewBanners = banners.some(banner => banner.isNew && (banner.title || banner.description || banner.imageFile));
      const hasUpdatedBanners = banners.some(banner => !banner.isNew && banner.isDirty && banner.imageFile);
      const hasTextOnlyUpdates = banners.some(banner => !banner.isNew && banner.isDirty && !banner.imageFile);

      if (!hasNewBanners && !hasUpdatedBanners) {
        if (hasTextOnlyUpdates) {
          alert('기존 배너의 제목이나 설명만 수정한 경우, 이미지도 다시 업로드해야 저장됩니다.\n이미지를 다시 선택한 후 저장해주세요.');
          return;
        } else {
          alert('저장할 변경사항이 없습니다.');
          return;
        }
      }

      let savedCount = 0;

      for (const banner of banners) {
        // 새 배너이면서 내용이 있는 경우 생성
        if (banner.isNew && (banner.title || banner.description || banner.imageFile)) {
          if (!banner.imageFile) {
            throw new Error(`배너 "${banner.title || '제목 없음'}"의 이미지를 선택해주세요.`);
          }

          // 배너 생성 요청
          const bannerRequest: CoffeeChatBannerCreateRequest = {
            title: banner.title,
            description: banner.description,
            fontcolorHexCode: banner.color || banner.font_color_hex_code,
            bannerImage: banner.imageFile
          };

          await createCoffeeChatBanner(bannerRequest);
          savedCount++;
        }
        // 기존 배너이면서 새 이미지가 업로드된 경우 업데이트
        else if (!banner.isNew && banner.isDirty && typeof banner.id === 'number' && banner.imageFile) {
          // console.log('배너 업데이트 시작:', {
          //   id: banner.id,
          //   title: banner.title,
          //   description: banner.description,
          //   color: banner.color,
          //   imageFile: banner.imageFile,
          //   isDirty: banner.isDirty
          // });

          // 배너 업데이트 요청
          const bannerRequest: CoffeeChatBannerUpdateRequest = {
            title: banner.title,
            description: banner.description,
            fontcolorHexCode: banner.color || banner.font_color_hex_code,
            bannerImage: banner.imageFile
          };

          /// console.log('배너 업데이트 요청 데이터:', bannerRequest);
          const result = await updateCoffeeChatBanner(banner.id as number, bannerRequest);
          /// console.log('배너 업데이트 결과:', result);
          savedCount++;
        }
      }

      // 저장 후 목록 새로고침
      await loadCoffeeChatUIData();
      
      if (savedCount > 0) {
        alert(`${savedCount}개의 커피챗 배너가 성공적으로 저장되었습니다.`);
        if (hasTextOnlyUpdates) {
          alert('참고: 이미지 없이 텍스트만 수정된 배너는 저장되지 않았습니다.\n해당 배너의 이미지를 다시 업로드한 후 저장해주세요.');
        }
      }
    } catch (err: any) {
      /// console.error('커피챗 배너 저장 실패:', err);
      setError(err.message || '커피챗 배너 저장에 실패했습니다.');
      alert(err.message || '커피챗 배너 저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(banners);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setBanners(items);
  };

  return (
    <Section>
      <SectionHeader>
        <SectionTitle>배너</SectionTitle>
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </SectionHeader>

      {loading && <LoadingMessage>로딩 중...</LoadingMessage>}

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="banners">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {banners.map((banner, index) => (
                <Draggable key={banner.id} draggableId={String(banner.id)} index={index}>
                  {(provided, snapshot) => (
                    <BannerContent
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={{
                        ...provided.draggableProps.style,
                        border: snapshot.isDragging ? '2px dashed #448181' : 'none',
                        borderRadius: snapshot.isDragging ? '8px' : '0',
                        backgroundColor: snapshot.isDragging ? '#f8f9fa' : 'transparent',
                        paddingBottom: '4px',
                        marginBottom: '20px'
                      }}
                    >
                      {/* 작은 드래그 핸들 추가 */}
                      <DragHandle
                        {...provided.dragHandleProps}
                        title={`배너 ${index + 1} 드래그`}
                      >
                        <DragHandleIcon />
                      </DragHandle>

                      <BannerImage>
                        <ImageUpload
                          className="bannerImage"
                          imageUrl={banner.imageUrl}
                          onImageChange={(url, file) => {
                            if (file) {
                              updateBannerLocal(banner.id, 'imageFile', file);
                            }
                          }}
                        />
                      </BannerImage>
                      <BannerForm>
                        <TitleRow>
                          <InputField
                            placeholder={`배너 ${index + 1} 제목을 입력해주세요.`}
                            className="titleInput"
                            value={banner.title}
                            onChange={(value) => updateBannerLocal(banner.id, 'title', value)}
                          />
                          <ColorInputWrapper>
                            <ColorPicker
                              type="color"
                              value={banner.color || banner.font_color_hex_code}
                              onChange={(e) => updateBannerLocal(banner.id, 'color', e.target.value)}
                            />
                            <ColorTextInput
                              type="text"
                              value={banner.color || banner.font_color_hex_code}
                              onChange={(e) => {
                                const colorValue = e.target.value;
                                if (colorValue.length > 0 && !colorValue.startsWith('#')) {
                                  updateBannerLocal(banner.id, 'color', '#' + colorValue);
                                } else {
                                  if (colorValue.length <= 7) {
                                    updateBannerLocal(banner.id, 'color', colorValue);
                                  }
                                }
                              }}
                              placeholder="#000000"
                            />
                          </ColorInputWrapper>
                        </TitleRow>
                        <CaptionRow>
                          <InputField
                            placeholder={`배너 ${index + 1} 설명을 입력해주세요.`}
                            className="captionInput"
                            value={banner.description}
                            onChange={(value) => updateBannerLocal(banner.id, 'description', value)}
                          />
                          <ActionButton
                            variant="outlined"
                            onClick={addBanner}
                            $isDisabled={banners.length >= 4}
                            disabled={banners.length >= 4}
                          >
                            <PlusIcon />
                          </ActionButton>
                          <ActionButton
                            variant="outlined"
                            onClick={() => removeBanner(banner.id)}
                            $isDisabled={banners.length <= 1}
                            disabled={banners.length <= 1}
                          >
                            <MinusIcon />
                          </ActionButton>
                        </CaptionRow>
                      </BannerForm>
                    </BannerContent>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* 배너 저장 버튼 */}
      <SectionActions>
        <SaveButton
          as="button"
          variant="primary"
          onClick={saveAllBanners}
          disabled={loading}
        >
          {loading ? '저장 중...' : '배너 저장'}
        </SaveButton>
      </SectionActions>
    </Section>
  );
};

// 커피챗 큐레이션 관리 컴포넌트 (홈 관리와 동일한 UI)
const CurationSection: React.FC<{ onRefreshSections: () => Promise<any> }> = ({ onRefreshSections }) => {
  const [curationSets, setCurationSets] = useState<CoffeeChatCurationSet[]>([]);
  const [availableMentors, setAvailableMentors] = useState<MentorInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mentorModalOpen, setMentorModalOpen] = useState<string | null>(null);

  // 멘토 목록 조회
  const loadMentors = async () => {
    try {
      const mentorResponse = await getCoffeeChatMentorList(false, undefined, undefined, 0, 100); // 승인된 멘토만 조회
      setAvailableMentors(mentorResponse.content);
    } catch (err: any) {
      /// console.error('멘토 목록 조회 실패:', err);
    }
  };

  // 커피챗 큐레이션 목록 조회
  const loadCurations = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const curationResponse = await getCoffeeChatCurationList();
      
      /// console.log('커피챗 큐레이션 API 응답:', curationResponse);
      
      // API 응답이 배열 자체인 경우와 sections 속성을 가진 경우 모두 처리
      const sectionsData = Array.isArray(curationResponse) ? curationResponse : curationResponse.sections || [];
      
      // API 데이터를 로컬 형식으로 변환
      const localCurations: CoffeeChatCurationSet[] = sectionsData.map((curation: any) => ({
        id: curation.coffee_chat_section_id,
        title: curation.title,
        description: curation.text,
        mentors: (curation.related_persons || []).map((person: any) => ({
          id: person.person_id,
          relation_id: person.section_related_person_id, // relation_id 포함
          name: person.name,
          department: person.department,
          image_url: person.image_url,
          tags: typeof person.tags === 'string' ? (person.tags as string).split(',').map((tag: string) => tag.trim()) : (person.tags as string[]) || []
        })),
        isNew: false,
        isDirty: false
      }));

      setCurationSets(localCurations.length > 0 ? localCurations : [createEmptyCoffeeChatCuration()]);
    } catch (err: any) {
      /// console.error('커피챗 큐레이션 목록 조회 실패:', err);
      setError(err.message || '커피챗 큐레이션 목록을 불러올 수 없습니다.');
      setCurationSets([createEmptyCoffeeChatCuration()]);
    } finally {
      setLoading(false);
    }
  };

  // 빈 큐레이션 생성
  const createEmptyCoffeeChatCuration = (): CoffeeChatCurationSet => ({
    id: `temp-${Date.now()}`,
    title: '',
    description: '',
    mentors: [],
    isNew: true,
    isDirty: false
  });

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    loadMentors();
    loadCurations();
  }, [ ]);

  // 큐레이션 세트 추가
  const addCurationSet = () => {
    const newSet = createEmptyCoffeeChatCuration();
    setCurationSets(prev => [...prev, newSet]);
  };

  // 큐레이션 세트 제거
  const removeCurationSet = async (setId: string | number) => {
    if (curationSets.length <= 1) return;

    try {
      const curation = curationSets.find(c => c.id === setId);
      if (!curation) return;

      // 서버에 저장된 큐레이션인 경우 API 호출
      if (typeof setId === 'number' && !curation.isNew) {
        await deleteCoffeeChatCuration(setId);
      }

      setCurationSets(prev => prev.filter(set => set.id !== setId));
      
      // 섹션 정보 새로고침
      await onRefreshSections();
    } catch (err: any) {
      /// console.error('커피챗 큐레이션 섹션 삭제 실패:', err);
      alert(err.message || '커피챗 큐레이션 섹션 삭제에 실패했습니다.');
    }
  };

  // 큐레이션 세트 업데이트
  const updateCurationSet = (setId: string | number, field: 'title' | 'description', value: string) => {
    setCurationSets(prev => prev.map(set => {
      if (set.id !== setId) return set;
      
      // 값이 실제로 변경되었는지 확인
      const currentValue = field === 'title' ? set.title : set.description;
      if (currentValue === value) return set; // 변경사항이 없으면 그대로 반환
      
      return { 
        ...set, 
        [field]: value, 
        isDirty: true // 변경사항이 있으면 isDirty를 true로 설정
      };
    }));
  };

  // 멘토를 큐레이션에 추가
  const addMentorToCurationSet = async (setId: string | number, mentor: MentorInfo) => {
    try {
      const curation = curationSets.find(c => c.id === setId);
      if (!curation) {
        /// console.error('큐레이션을 찾을 수 없음:', setId);
        return;
      }

      // console.log('멘토 추가 시도:', { 
      //   setId, 
      //   mentorId: mentor.id, 
      //   isNew: curation.isNew,
      //   curationTitle: curation.title,
      //   existingMentors: curation.mentors.map(m => m.id)
      // });

      // 이미 추가된 멘토인지 확인
      if (curation.mentors.some(m => m.id === mentor.id)) {
        alert('이미 추가된 멘토입니다.');
        return;
      }

      // 새로운 큐레이션인 경우 먼저 큐레이션을 저장하라고 안내
      if (curation.isNew) {
        // /// console.log('새로운 큐레이션이므로 저장 필요');
        alert('멘토를 추가하기 전에 먼저 큐레이션 섹션을 저장해주세요.\\n\\n1. 제목과 설명을 입력하세요\\n2. "큐레이션 저장" 버튼을 클릭하세요\\n3. 저장 완료 후 멘토를 추가할 수 있습니다');
        return;
      }

      // 서버에 저장된 큐레이션인 경우 즉시 멘토 추가
      if (typeof setId === 'number') {
        /// console.log('기존 큐레이션에 멘토 추가 (API 호출):', { setId, mentorId: mentor.id });
        
        try {
          const apiResponse = await addPersonToCoffeeChatCuration(setId, mentor.id);

          /// console.log('멘토 추가 API 응답:', apiResponse);

          // API 호출 성공 후 로컬 상태 업데이트
          setCurationSets(prev => prev.map(set => {
            if (set.id !== setId) return set;

            const newMentor = {
              id: mentor.id,
              relation_id: apiResponse.relation_id, // API 응답에서 받은 relation_id 사용
              name: mentor.name,
              department: mentor.department,
              image_url: mentor.image_url,
              tags: mentor.tags || []
            };

            return {
              ...set,
              mentors: [...set.mentors, newMentor],
              isDirty: false // API 호출했으므로 더티 상태 해제
            };
          }));

          setMentorModalOpen(null);
          
          // 섹션 정보 새로고침
          await onRefreshSections();
          alert('멘토가 성공적으로 추가되었습니다.');
          
        } catch (apiError: any) {
          /// console.error('API 에러 상세:', apiError);
          
          // 409 에러 (이미 존재하는 관계)인 경우
          if (apiError.response?.status === 409 || apiError.status === 409 || apiError.message?.includes('이미 존재') || apiError.message?.includes('already exists') || apiError.message?.includes('duplicate')) {
            alert('이 멘토는 이미 해당 큐레이션에 등록되어 있습니다.\\n\\n페이지를 새로고침하여 최신 상태를 확인해주세요.');
            // 큐레이션 목록 새로고침
            await loadCurations();
            setMentorModalOpen(null);
          } else if (apiError.response?.status === 400 || apiError.status === 400) {
            alert('잘못된 요청입니다. 멘토 정보나 큐레이션 정보를 다시 확인해주세요.');
          } else if (apiError.response?.status === 404 || apiError.status === 404) {
            alert('멘토를 찾을 수 없습니다. 멘토가 삭제되었거나 존재하지 않을 수 있습니다.');
          } else {
            throw apiError; // 다른 에러는 그대로 throw
          }
        }
      }
    } catch (err: any) {
      /// console.error('멘토 추가 실패:', err);
      alert(err.message || '멘토 추가에 실패했습니다.');
    }
  };

  // 멘토를 큐레이션에서 제거
  const removeMentorFromCurationSet = async (setId: string | number, mentorId: number) => {
    try {
      const curation = curationSets.find(c => c.id === setId);
      if (!curation) return;

      // 새로운 큐레이션이거나 서버에 저장되지 않은 상태인 경우 로컬에서만 제거
      if (curation.isNew) {
        /// console.log('새로운 큐레이션에서 멘토 제거 (로컬만):', { setId, mentorId });
        
        // 로컬 상태만 업데이트
        setCurationSets(prev => prev.map(set => 
          set.id === setId 
            ? { ...set, mentors: set.mentors.filter(m => m.id !== mentorId), isDirty: true }
            : set
        ));
        return;
      }

        // 서버에 저장된 큐레이션인 경우 API 호출
        if (typeof setId === 'number') {
          /// console.log('기존 큐레이션에서 멘토 제거 (API 호출):', { setId, mentorId });
          
          // personId로 API 호출
          await removePersonFromCoffeeChatCuration(setId, mentorId);

          // API 호출 성공 후 로컬 상태 업데이트
          setCurationSets(prev => prev.map(set => 
            set.id === setId 
              ? { ...set, mentors: set.mentors.filter(m => m.id !== mentorId), isDirty: false }
              : set
          ));

          // 섹션 정보 새로고침
          await onRefreshSections();
          alert('멘토가 성공적으로 제거되었습니다.');
        }
    } catch (err: any) {
      /// console.error('멘토 제거 실패:', err);
      alert(err.message || '멘토 제거에 실패했습니다.');
    }
  };

  // 개별 큐레이션 저장
  const saveSingleCuration = async (setId: string | number) => {
    try {
      const curation = curationSets.find(c => c.id === setId);
      if (!curation) return;

      /// console.log('커피챗 큐레이션 저장 시작:', { setId, curation });

      if (!curation.title.trim()) {
        alert('큐레이션 제목을 입력해주세요.');
        return;
      }

      if (!curation.description.trim()) {
        alert('큐레이션 설명을 입력해주세요.');
        return;
      }

      setLoading(true);

      if (curation.isNew) {
        // /// console.log('새로운 커피챗 큐레이션 생성 중...');
        // 새로운 큐레이션 생성
        const sectionData = {
          title: curation.title,
          text: curation.description
        };

        const createdSection = await createCoffeeChatCuration(sectionData);
        /// console.log('커피챗 큐레이션 생성 완료:', createdSection);
        
        // 로컬 상태 업데이트
        setCurationSets(prev => prev.map(set => 
          set.id === setId 
            ? { 
                ...set, 
                id: createdSection.id ?? createdSection.coffee_chat_section_id,
                isNew: false, 
                isDirty: false 
              }
            : set
        ));

        // 섹션 정보 새로고침
        await onRefreshSections();
        alert('커피챗 큐레이션이 성공적으로 저장되었습니다.\\n이제 멘토를 추가할 수 있습니다.');
      } else if (typeof setId === 'number' && curation.isDirty) {
        // /// console.log('기존 커피챗 큐레이션 업데이트 중...');
        // 기존 큐레이션 업데이트
        const sectionData = {
          title: curation.title,
          text: curation.description
        };

        await updateCoffeeChatCuration(setId, sectionData);
        /// console.log('커피챗 큐레이션 업데이트 완료');
        
        // 로컬 상태 업데이트
        setCurationSets(prev => prev.map(set => 
          set.id === setId 
            ? { ...set, isDirty: false }
            : set
        ));

        // 섹션 정보 새로고침
        await onRefreshSections();
        alert('커피챗 큐레이션이 성공적으로 업데이트되었습니다.');
      } else {
        alert('저장할 변경사항이 없습니다.');
      }
    } catch (err: any) {
      /// console.error('커피챗 큐레이션 저장 실패:', err);
      alert(err.message || '커피챗 큐레이션 저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 모든 큐레이션 저장
  const saveAllCurations = async () => {
    try {
      setLoading(true);
      setError(null);

      let savedCount = 0;

      for (const curation of curationSets) {
        // 새 큐레이션이면서 내용이 있는 경우에만 생성 (멘토가 없는 빈 큐레이션만)
        if (curation.isNew && (curation.title || curation.description) && curation.mentors.length === 0) {
          const sectionData = {
            title: curation.title || '새 커피챗 큐레이션',
            text: curation.description || '새 커피챗 큐레이션 설명'
          };

          await createCoffeeChatCuration(sectionData);
          savedCount++;
        }
        // 기존 큐레이션이면서 수정된 경우 업데이트
        else if (!curation.isNew && curation.isDirty && typeof curation.id === 'number') {
          const sectionData = {
            title: curation.title,
            text: curation.description
          };

          await updateCoffeeChatCuration(curation.id, sectionData);
          savedCount++;
        }
      }

      // 저장 후 목록 새로고침
      await loadCurations();
      
      // 섹션 정보도 새로고침
      await onRefreshSections();
      
      if (savedCount > 0) {
        alert(`${savedCount}개의 커피챗 큐레이션이 성공적으로 저장되었습니다.`);
      } else {
        alert('저장할 변경사항이 없습니다.\\n\\n참고: 멘토가 추가된 새 큐레이션은 이미 자동으로 저장되었습니다.');
      }
    } catch (err: any) {
      /// console.error('커피챗 큐레이션 저장 실패:', err);
      setError(err.message || '커피챗 큐레이션 저장에 실패했습니다.');
      alert(err.message || '커피챗 큐레이션 저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section>
      <SectionHeader>
        <SectionTitle>커피챗 큐레이션</SectionTitle>
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </SectionHeader>

      {loading && <LoadingMessage>로딩 중...</LoadingMessage>}

      {curationSets.map((curationSet, setIndex) => (
        <CurationContent key={curationSet.id} style={{ marginBottom: '32px' }}>
          <CurationForm>
            <FormGroup>
              <SectionTitle as="label" style={{ fontSize: '12px', color: 'rgba(46, 47, 51, 0.6)' }}>
                커피챗 큐레이션 {setIndex + 1}
              </SectionTitle>
              <InputField
                placeholder="제목을 입력해주세요."
                value={curationSet.title}
                onChange={(value) => updateCurationSet(curationSet.id, 'title', value)}
              />
            </FormGroup>
            <InputField
              placeholder="설명을 입력해주세요."
              className="descriptionInput"
              value={curationSet.description}
              onChange={(value) => updateCurationSet(curationSet.id, 'description', value)}
            />
            <CurationButtons>
              {/* 개별 큐레이션 저장 버튼 */}
              <SaveIndividualButton
                variant={curationSet.isNew || curationSet.isDirty ? "primary" : "outlined"}
                onClick={() => saveSingleCuration(curationSet.id)}
                disabled={loading}
              >
                {loading ? '저장 중...' :
                 curationSet.isNew ? '큐레이션 저장' :
                 curationSet.isDirty ? '변경사항 저장' : '저장됨'}
              </SaveIndividualButton>
              <ActionButton
                variant="outlined"
                onClick={addCurationSet}
              >
                <PlusIcon />
              </ActionButton>
              <ActionButton
                variant="outlined"
                onClick={() => removeCurationSet(curationSet.id)}
                $isDisabled={curationSets.length <= 1}
                disabled={curationSets.length <= 1}
              >
                <MinusIcon />
              </ActionButton>
            </CurationButtons>
          </CurationForm>

          {/* 멘토 목록 표시 */}
          <LectureGrid>
            {curationSet.mentors.map((mentor) => (
              <LectureCard key={mentor.id}>
                <LectureCardImage>
                  {mentor.image_url ? (
                    <img src={mentor.image_url} alt={mentor.name} />
                  ) : (
                    <NoImage>이미지 없음</NoImage>
                  )}
                </LectureCardImage>
                <LectureCardContent>
                  <LectureTitle>{mentor.name}</LectureTitle>
                  <LecturerName>{mentor.department}</LecturerName>
                  {mentor.tags && mentor.tags.length > 0 && (
                    <LectureTags>
                      {mentor.tags.map((tag, idx) => (
                        <Tag key={idx}>{tag.trim()}</Tag>
                      ))}
                    </LectureTags>
                  )}
                </LectureCardContent>
                <RemoveLectureButton
                  className="remove-lecture-btn"
                  onClick={() => removeMentorFromCurationSet(curationSet.id, mentor.id)}
                >
                  <MinusIcon />
                </RemoveLectureButton>
              </LectureCard>
            ))}

            {/* 멘토 추가 버튼 */}
            <AddLectureCard
              $disabled={curationSet.isNew}
              onClick={() => {
                // console.log('멘토 추가 버튼 클릭:', {
                //   setId: curationSet.id,
                //   isNew: curationSet.isNew,
                //   title: curationSet.title,
                //   description: curationSet.description
                // });

                if (curationSet.isNew) {
                  alert('멘토를 추가하기 전에 먼저 큐레이션을 저장해주세요.');
                  return;
                }
                setMentorModalOpen(String(curationSet.id));
              }}
              title={curationSet.isNew ? '큐레이션을 먼저 저장해주세요' : '멘토 추가'}
            >
              <PlusIcon />
              <span>멘토 추가</span>
              {curationSet.isNew && (
                <small style={{ fontSize: '10px', color: '#999', marginTop: '4px' }}>
                  (큐레이션 저장 후 가능)
                </small>
              )}
            </AddLectureCard>
          </LectureGrid>

          {/* 멘토 선택 모달 */}
          {mentorModalOpen === String(curationSet.id) && (
            <LectureModal>
              <ModalOverlay onClick={() => setMentorModalOpen(null)} />
              <ModalContent>
                <ModalHeader>
                  <h3>멘토 선택</h3>
                  <button onClick={() => setMentorModalOpen(null)}>×</button>
                </ModalHeader>
                <ModalBody>
                  <LectureList>
                    {availableMentors.map((mentor) => {
                      const isAlreadyAdded = curationSet.mentors.some(m => m.id === mentor.id);

                      return (
                        <LectureListItem
                          key={mentor.id}
                          $alreadyAdded={isAlreadyAdded}
                          onClick={() => {
                            if (isAlreadyAdded) {
                              alert('이 멘토는 이미 추가되어 있습니다.');
                              return;
                            }
                            addMentorToCurationSet(curationSet.id, mentor);
                          }}
                          title={isAlreadyAdded ? '이미 추가된 멘토' : '클릭하여 추가'}
                        >
                          <LectureListImage>
                            {mentor.image_url ? (
                              <img src={mentor.image_url} alt={mentor.name} />
                            ) : (
                              <NoImage>이미지 없음</NoImage>
                            )}
                          </LectureListImage>
                          <LectureListContent>
                            <h4>{mentor.name}</h4>
                            <p>{mentor.department}</p>
                            {mentor.tags && mentor.tags.length > 0 && (
                              <LectureTags>
                                {mentor.tags.map((tag: string, idx: number) => (
                                  <Tag key={idx}>{tag}</Tag>
                                ))}
                              </LectureTags>
                            )}
                          </LectureListContent>
                          {isAlreadyAdded && (
                            <AddedIndicator>
                              ✓ 추가됨
                            </AddedIndicator>
                          )}
                        </LectureListItem>
                      );
                    })}
                  </LectureList>
                </ModalBody>
              </ModalContent>
            </LectureModal>
          )}
        </CurationContent>
      ))}

      {/* 큐레이션 저장 버튼 */}
      <SectionActions>
        <SaveButton
          as="button"
          variant="primary"
          onClick={saveAllCurations}
          disabled={loading}
        >
          {loading ? '저장 중...' : '커피챗 큐레이션 저장'}
        </SaveButton>
      </SectionActions>
    </Section>
  );
};

const CoffeeChatManagement: React.FC = () => {
  // 섹션 상태를 상위에서 관리
  const [sections, setSections] = useState<CoffeeChatSection[]>([]);

  // 빈 배너 생성 헬퍼 함수
  const createEmptyBanner = (): LocalBanner => ({
    id: `temp-${Date.now()}`,
    title: "",
    description: "",
    banner_image_url: "",
    font_color_hex_code: "#000000",
    color: "#000000",
    imageUrl: "",
    isNew: true,
    isDirty: false
  });

  // 커피챗 UI 데이터 조회 함수를 상위로 이동
  const loadCoffeeChatUIData = React.useCallback(async () => {
    try {
      // /// console.log('관리자 커피챗 UI 데이터 조회 시작...');
      const uiResponse = await getAdminCoffeeChatUI();
      /// console.log('API에서 받은 커피챗 UI 데이터:', uiResponse);
      
      // 배너 데이터 변환 (API에서 값이 바뀌어서 온 경우 수정)
      const localBanners: LocalBanner[] = uiResponse.data.banners.map((banner: CoffeeChatBanner) => {
        // API 응답에서 banner_image_url과 font_color_hex_code가 바뀌어서 올 수 있으므로 수정
        const actualImageUrl = banner.font_color_hex_code.startsWith('http') ? banner.font_color_hex_code : banner.banner_image_url;
        const actualColorCode = banner.banner_image_url.startsWith('#') ? banner.banner_image_url : banner.font_color_hex_code;

        // console.log('배너 데이터 수정:', {
        //   originalBannerImageUrl: banner.banner_image_url,
        //   originalFontColorCode: banner.font_color_hex_code,
        //   correctedImageUrl: actualImageUrl,
        //   correctedColorCode: actualColorCode
        // });

        return {
          id: banner.banner_id,
          title: banner.title,
          description: banner.description,
          banner_image_url: actualImageUrl,
          font_color_hex_code: actualColorCode,
          // UI용 필드도 추가
          color: actualColorCode,
          imageUrl: actualImageUrl,
          isNew: false,
          isDirty: false
        };
      });

      /// console.log('변환된 로컬 배너:', localBanners);
      /// console.log('받은 섹션 데이터:', uiResponse.data.sections);

      // 섹션 상태 업데이트
      setSections(uiResponse.data.sections || []);

      return {
        banners: localBanners.length > 0 ? localBanners : [createEmptyBanner()],
        sections: uiResponse.data.sections || []
      };
      
    } catch (err: any) {
      /// console.error('커피챗 UI 데이터 조회 실패:', err);
      
      // 실패 시 기존 개별 API로 폴백
      try {
        const apiBanners = await getCoffeeChatBannerList();
        /// console.log('개별 배너 API 결과:', apiBanners);
        
        const localBanners: LocalBanner[] = apiBanners.map((banner: CoffeeChatBanner) => {
          // API 응답에서 banner_image_url과 font_color_hex_code가 바뀌어서 올 수 있으므로 수정
          const actualImageUrl = banner.font_color_hex_code.startsWith('http') ? banner.font_color_hex_code : banner.banner_image_url;
          const actualColorCode = banner.banner_image_url.startsWith('#') ? banner.banner_image_url : banner.font_color_hex_code;

          // console.log('폴백 배너 데이터 수정:', {
          //   originalBannerImageUrl: banner.banner_image_url,
          //   originalFontColorCode: banner.font_color_hex_code,
          //   correctedImageUrl: actualImageUrl,
          //   correctedColorCode: actualColorCode
          // });

          return {
            id: banner.banner_id,
            title: banner.title,
            description: banner.description,
            banner_image_url: actualImageUrl,
            font_color_hex_code: actualColorCode,
            color: actualColorCode,
            imageUrl: actualImageUrl,
            isNew: false,
            isDirty: false
          };
        });

        // 섹션 상태 업데이트 (폴백 시에는 빈 배열)
        setSections([]);

        return {
          banners: localBanners.length > 0 ? localBanners : [createEmptyBanner()],
          sections: []
        };
      } catch (fallbackErr: any) {
        /// console.error('개별 배너 API도 실패:', fallbackErr);
        // 섹션 상태 업데이트 (최종 폴백 시에도 빈 배열)
        setSections([]);
        return {
          banners: [createEmptyBanner()],
          sections: []
        };
      }
    }
  }, []);

  return (
    <AdminPage>
      {/* 기존 타이틀 유지 */}
      <AdminPageHeader>
        <AdminPageTitle>커피챗 관리</AdminPageTitle>
      </AdminPageHeader>

      <AdminSection>
        {/* 배너 섹션 */}
        <BannerSection
          onRefreshSections={loadCoffeeChatUIData}
          createEmptyBanner={createEmptyBanner}
          sections={sections}
        />

        {/* 큐레이션 섹션 */}
        <CurationSection onRefreshSections={loadCoffeeChatUIData} />
      </AdminSection>
    </AdminPage>
  );
};

export default CoffeeChatManagement;
